import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Gift, Trophy, Users, Search, Filter, MoreVertical, 
  Eye, Trash2, Award, ChevronLeft, ChevronRight, Loader2, 
  Calendar, DollarSign, MapPin, Clock, AlertCircle, CheckCircle,
  Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminGiveaways = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  
  // Dialogs
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [entriesDialogOpen, setEntriesDialogOpen] = useState(false);
  const [winnerDialogOpen, setWinnerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [winnerSelecting, setWinnerSelecting] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);

  const fetchGiveaways = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        skip: pagination.skip.toString()
      });
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await fetch(`${API}/api/admin/giveaways?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch giveaways');
      
      const data = await response.json();
      setGiveaways(data.giveaways || []);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error) {
      toast.error('Failed to load giveaways');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.skip, pagination.limit, statusFilter]);

  useEffect(() => {
    fetchGiveaways();
  }, [fetchGiveaways]);

  const fetchEntries = async (giveawayId) => {
    try {
      setEntriesLoading(true);
      const response = await fetch(`${API}/api/admin/giveaways/${giveawayId}/entries?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch entries');
      
      const data = await response.json();
      setEntries(data.entries || []);
    } catch (error) {
      toast.error('Failed to load entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleViewEntries = async (giveaway) => {
    setSelectedGiveaway(giveaway);
    setEntriesDialogOpen(true);
    await fetchEntries(giveaway.giveaway_id);
  };

  const handleSelectWinner = async () => {
    if (!selectedGiveaway) return;
    
    try {
      setWinnerSelecting(true);
      const response = await fetch(`${API}/api/admin/giveaways/${selectedGiveaway.giveaway_id}/select-winner`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to select winner');
      }
      
      const winner = await response.json();
      setSelectedWinner(winner);
      toast.success(`Winner selected: ${winner.winner_name}`);
      
      // Refresh giveaways
      fetchGiveaways();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWinnerSelecting(false);
    }
  };

  const handleDeleteGiveaway = async () => {
    if (!selectedGiveaway) return;
    
    try {
      const response = await fetch(`${API}/api/admin/giveaways/${selectedGiveaway.giveaway_id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete giveaway');
      
      toast.success('Giveaway deleted');
      setDeleteDialogOpen(false);
      setSelectedGiveaway(null);
      fetchGiveaways();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredGiveaways = giveaways.filter(g => 
    g.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      ended: 'bg-gray-100 text-gray-700',
      fulfilled: 'bg-blue-100 text-blue-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-giveaways-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Giveaway Management</h1>
          <p className="text-neutral-500 mt-1">Manage giveaways, select winners, view entries</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 bg-[#FF3B30]/10 text-[#FF3B30] rounded-lg text-sm font-medium">
            {pagination.total} Total Giveaways
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search by title or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="giveaway-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="status-filter-select">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="ended">Ended</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Giveaways Table */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
          </div>
        ) : filteredGiveaways.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-neutral-500">
            <Gift className="w-12 h-12 mb-4 opacity-50" />
            <p>No giveaways found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600">Giveaway</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600">Location</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600">Value</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600">Entries</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600">Winner</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600">End Date</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filteredGiveaways.map((giveaway) => (
                  <tr key={giveaway.giveaway_id} className="hover:bg-neutral-50" data-testid={`giveaway-row-${giveaway.giveaway_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img 
                          src={giveaway.image_url} 
                          alt={giveaway.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                          <p className="font-medium text-neutral-900 line-clamp-1">{giveaway.title}</p>
                          <p className="text-sm text-neutral-500">{giveaway.project_type}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-neutral-600">
                        <MapPin className="w-4 h-4" />
                        <span>{giveaway.city}, {giveaway.province_state}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-neutral-900 font-medium">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        {giveaway.value?.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-neutral-400" />
                        <span className="font-medium">{giveaway.total_entries || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(giveaway.status)}
                    </td>
                    <td className="px-6 py-4">
                      {giveaway.winner ? (
                        <div className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-yellow-500" />
                          <span className="text-sm text-neutral-700">{giveaway.winner.name}</span>
                        </div>
                      ) : (
                        <span className="text-neutral-400 text-sm">Not selected</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600">
                      {formatDate(giveaway.end_date)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`giveaway-actions-${giveaway.giveaway_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewEntries(giveaway)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Entries
                          </DropdownMenuItem>
                          {!giveaway.winner_id && giveaway.total_entries > 0 && (
                            <DropdownMenuItem onClick={() => {
                              setSelectedGiveaway(giveaway);
                              setSelectedWinner(null);
                              setWinnerDialogOpen(true);
                            }}>
                              <Award className="w-4 h-4 mr-2" />
                              Select Winner
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => {
                              setSelectedGiveaway(giveaway);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200">
            <p className="text-sm text-neutral-500">
              Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.skip === 0}
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.skip + pagination.limit >= pagination.total}
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Entries Dialog */}
      <Dialog open={entriesDialogOpen} onOpenChange={setEntriesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Entries for {selectedGiveaway?.title}
            </DialogTitle>
            <DialogDescription>
              {entries.length} total entries
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {entriesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                No entries yet
              </div>
            ) : (
              <div className="divide-y divide-neutral-100">
                {entries.map((entry) => (
                  <div key={entry.entry_id} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-neutral-900">{entry.user?.name || 'Unknown'}</p>
                      <p className="text-sm text-neutral-500">{entry.user?.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-neutral-600">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {formatDate(entry.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Winner Selection Dialog */}
      <Dialog open={winnerDialogOpen} onOpenChange={setWinnerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              Select Winner
            </DialogTitle>
            <DialogDescription>
              Randomly select a winner from {selectedGiveaway?.total_entries || 0} entries
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedWinner ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-1">
                  {selectedWinner.winner_name}
                </h3>
                <p className="text-neutral-500">{selectedWinner.winner_email}</p>
                <p className="text-sm text-green-600 mt-4">
                  Winner has been notified via email!
                </p>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                  <Trophy className="w-8 h-8 text-yellow-600" />
                </div>
                <p className="text-neutral-600 mb-2">
                  Ready to pick a winner for:
                </p>
                <p className="font-bold text-lg text-neutral-900">
                  {selectedGiveaway?.title}
                </p>
                <p className="text-2xl font-bold text-green-600 mt-2">
                  ${selectedGiveaway?.value?.toLocaleString()} Prize
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            {!selectedWinner && (
              <Button
                onClick={handleSelectWinner}
                disabled={winnerSelecting}
                className="w-full bg-[#FF3B30] hover:bg-[#DC2626]"
                data-testid="select-winner-btn"
              >
                {winnerSelecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Selecting...
                  </>
                ) : (
                  <>
                    <Award className="w-4 h-4 mr-2" />
                    Pick Random Winner
                  </>
                )}
              </Button>
            )}
            {selectedWinner && (
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/giveaways/${selectedGiveaway?.giveaway_id}/share`)}
                  className="flex-1"
                  data-testid="share-win-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Win
                </Button>
                <Button
                  onClick={() => {
                    setWinnerDialogOpen(false);
                    setSelectedWinner(null);
                  }}
                  className="flex-1"
                >
                  Done
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Giveaway
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedGiveaway?.title}"? This action cannot be undone and will remove all associated entries.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteGiveaway}
              data-testid="confirm-delete-btn"
            >
              Delete Giveaway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGiveaways;
